// src/pages/PpdppPage.js
// Event landing page for the PPD Petaling Perdana × Zabra Solutions GPM briefing
// (21–23 Okt 2026). Reached via the QR on the final CTA slide → /ppdpp.
// Same look as /contact, plus: event header, countdown, locked coupon,
// Pilihan A/B offer selector, GPM prefill and its own WhatsApp template.
// After the deadline it shows a "tawaran tamat" banner and the normal form.
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';
import { ArrowDown, BadgeCheck, CalendarClock, Gift, Lock, Sparkles } from 'lucide-react';
import schoolsData from '../schoolsData.json';
import ContactPage from './ContactPage';

// ---- Campaign config (edit here) -------------------------------------------
const CAMPAIGN = {
  coupon: 'PPDPP26',
  eventName: 'PPD Petaling Perdana × Zabra Solutions',
  eventDates: '21–23 Okt 2026',
  // 25 Okt 2026, 11:59:59 malam waktu Malaysia
  deadline: new Date('2026-10-25T23:59:59+08:00'),
  deadlineLabel: '25 Oktober 2026',
  slotLockDate: '15 Nov 2026',
  whatsappNumber: '60173634766',
  bookPrice: 4700, // Pembelian buku (LITERA tahun pertama)
};

// Shown only AFTER submit — the school picks whichever suits it (kesesuaian
// sekolah), usually after confirming with kerani/pentadbir.
const OPTIONS = [
  {
    id: 'A',
    title: 'Bayar 2026',
    discount: 600,
    tag: '2× JIMAT',
    desc: 'Sesuai jika sekolah masih ada bajet tahun ini.',
    terms: 'Bayaran sebelum 31 Disember 2026.',
    wa: 'A – Beli Buku Tahun 2026',
    note: 'Jimat RM600',
  },
  {
    id: 'B',
    title: 'Bayar 2027',
    discount: 300,
    tag: '50 SEKOLAH TERAWAL',
    desc: 'Sesuai jika bajet tahun ini sudah habis. Setup dahulu, bayar tahun depan.',
    terms: `Slot dikira ikut Surat Setuju Terima ditandatangani sebelum ${CAMPAIGN.slotLockDate}. Bayaran sebelum 31 Mac 2027. Selepas 50 slot, masih boleh bayar 2027 tanpa diskaun.`,
    wa: 'B – Beli Buku Tahun 2027',
    note: 'Jimat RM300, 50 sekolah terawal',
  },
];

const rm = (n) => `RM${n.toLocaleString('en-MY')}`;

const POSITIONS = ['GPM', 'Pentadbir', 'Guru Penyelaras', 'Lain-lain'];

const supabase = createClient(
  'https://mmyfyclfttmppuxxtztg.supabase.co',
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im1teWZ5Y2xmdHRtcHB1eHh0enRnIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzYyMTY0MDIsImV4cCI6MjA1MTc5MjQwMn0.dmtItQQru0HX7HDR-0Sa4LtED_oYfYA_c9epwtU-CbU'
);

// ---- Countdown -------------------------------------------------------------
const useCountdown = (target) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, target.getTime() - now);
  return {
    expired: diff === 0,
    d: Math.floor(diff / 86400000),
    h: Math.floor((diff / 3600000) % 24),
    m: Math.floor((diff / 60000) % 60),
    s: Math.floor((diff / 1000) % 60),
  };
};

const pad = (n) => String(n).padStart(2, '0');

const inputCls = (err) =>
  `w-full p-3 rounded-lg border border-[var(--z-line)] bg-[var(--z-cream)] text-gray-800 focus:outline-none focus:border-[var(--z-accent)] focus:ring-2 focus:ring-[var(--z-accent-100)] transition ${err ? 'border-red-500' : ''}`;

// ---- Page ------------------------------------------------------------------
const PpdppPage = () => {
  const cd = useCountdown(CAMPAIGN.deadline);
  const schools = useMemo(() => schoolsData['selangor'] || [], []);

  const [name, setName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSchool, setSelectedSchool] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isManualEntry, setIsManualEntry] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [position, setPosition] = useState('GPM');
  const [under300, setUnder300] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [waUrl, setWaUrl] = useState('');
  const cardRef = useRef(null);
  // Scroll the WINDOW (not an inner overflow-hidden box) so the page can still be scrolled back up.
  const scrollToCard = (offset = 110) => {
    const el = cardRef.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.pageYOffset - offset;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  };
  const goToForm = () => scrollToCard();

  useEffect(() => {
    const prev = document.title;
    document.title = 'Tawaran Khas Sistem Pusat Sumber Zabra (LITERA) · PPD Petaling Perdana';
    return () => { document.title = prev; };
  }, []);

  const clearErr = (k) => setErrors((e) => ({ ...e, [k]: '' }));

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setSelectedSchool(value);
    clearErr('school');
    if (value.length > 1 && !isManualEntry) {
      const q = value.toLowerCase();
      setSuggestions(schools.filter((s) => s.toLowerCase().includes(q)).slice(0, 5));
    } else {
      setSuggestions([]);
    }
  };

  const pickSchool = (school) => {
    setSelectedSchool(school);
    setSearchTerm(school);
    setSuggestions([]);
    clearErr('school');
  };

  const toggleManualEntry = () => {
    setIsManualEntry((v) => !v);
    setSearchTerm('');
    setSelectedSchool('');
    setSuggestions([]);
  };

  const handlePhone = (e) => {
    const v = e.target.value.replace(/\D/g, '');
    if (v.length > 11) return;
    let f = v;
    if (v.length > 3) f = v.slice(0, 3) + '-' + (v.length > 7 ? v.slice(3, 7) + '-' + v.slice(7) : v.slice(3));
    setPhoneNumber(f);
    clearErr('phoneNumber');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = {};
    if (!name.trim()) err.name = 'Nama diperlukan';
    if (!selectedSchool.trim()) err.school = 'Sekolah diperlukan';
    if (!phoneNumber) err.phoneNumber = 'Nombor telefon diperlukan';
    if (!position) err.position = 'Jawatan diperlukan';
    if (Object.keys(err).length) {
      setErrors(err);
      const first = document.getElementById(Object.keys(err)[0]);
      if (first) window.scrollTo({ top: Math.max(0, first.getBoundingClientRect().top + window.pageYOffset - window.innerHeight / 2), behavior: 'smooth' });
      return;
    }

    setIsSubmitting(true);
    const base = { name, state: 'selangor', school: selectedSchool, phone_number: phoneNumber };

    // Save the lead. Try with campaign columns first; if the table doesn't have
    // them yet, fall back to the existing columns and tag the campaign into
    // `position`. Never block the WhatsApp hand-off on a DB error.
    try {
      const { error } = await supabase.from('contact_submissions').insert([
        { ...base, position, coupon: CAMPAIGN.coupon, under_300: under300 },
      ]);
      if (error) {
        await supabase.from('contact_submissions').insert([
          { ...base, position: `${position} [${CAMPAIGN.coupon}${under300 ? ' <300 murid' : ''}]` },
        ]);
      }
    } catch (dbErr) {
      console.error('Supabase insert failed:', dbErr);
    }

    const sentAt = new Date().toLocaleString('ms-MY', {
      timeZone: 'Asia/Kuala_Lumpur',
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });

    const message =
`🎟️ *DAFTAR MINAT TAWARAN KHAS – PPD PETALING PERDANA*

Assalamualaikum dan Salam Sejahtera, saya berminat dengan tawaran khas Sistem Pusat Sumber Zabra (LITERA).

*Persediaan Awal, Permulaan Digital 2027*
Setup selesai tahun ini, guru & murid siap sedia menjelang Januari.

*Kupon:* ${CAMPAIGN.coupon}

Nama: ${name}
Sekolah: ${selectedSchool}
Jawatan: ${position}
No. Telefon: ${phoneNumber}${under300 ? '\n\n✅ *Sekolah saya kurang daripada 300 murid* (mohon tawaran khas)' : ''}

*Pilihan tawaran (ikut kesesuaian sekolah):*
${OPTIONS.map((o) => `${o.wa}\nPembelian buku ~${rm(CAMPAIGN.bookPrice)}~ *${rm(CAMPAIGN.bookPrice - o.discount)}* (${o.note})`).join('\n\n')}

Dihantar: ${sentAt}`;

    // Show a short thank-you + loading state, then hand off to WhatsApp.
    const url = `https://wa.me/${CAMPAIGN.whatsappNumber}?text=${encodeURIComponent(message)}`;
    setWaUrl(url);
    setIsSubmitting(false);
    // Bring the thank-you card into view (not the top of the page).
    requestAnimationFrame(() => scrollToCard(140));
    setTimeout(() => { window.location.href = url; }, 1200);
  };

  // ---- Expired: banner + normal enquiry form --------------------------------
  if (cd.expired) {
    return (
      <div>
        <div className="bg-[var(--z-ink)] text-white pt-32 pb-6 px-4 text-center">
          <p className="z-eyebrow !bg-white/10 !border-white/20 !text-white/80">PPD Petaling Perdana</p>
          <h1 className="font-serif-display text-2xl md:text-3xl font-semibold mt-3">Tawaran telah tamat</h1>
          <p className="text-white/70 text-sm mt-2 max-w-md mx-auto">
            Tawaran khas berakhir pada {CAMPAIGN.deadlineLabel}. Terima kasih atas minat anda. Anda masih boleh
            menghubungi kami melalui borang di bawah.
          </p>
        </div>
        <ContactPage />
      </div>
    );
  }

  // ---- Live campaign --------------------------------------------------------
  return (
    <div className="monogram-orange min-h-screen pt-28 md:pt-36 pb-20 px-4" style={{ overflow: 'clip' }}>
      <div className="max-w-lg mx-auto">
        {/* Event header — compact so the form shows on the first phone screen */}
        <div className="relative overflow-hidden rounded-3xl bg-[var(--z-ink)] text-white p-5 md:p-7 shadow-2xl">
          <div
            aria-hidden
            className="absolute -top-24 -right-24 w-56 h-56 rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(242,146,0,.45), rgba(242,146,0,0) 70%)' }}
          />
          <div className="relative">
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
              <span className="inline-flex items-center gap-1.5 text-[10px] font-extrabold tracking-[0.16em] uppercase bg-[var(--z-accent)] text-white px-2.5 py-1 rounded-full">
                <Sparkles size={12} /> Edisi Khas
              </span>
              <span className="text-xs font-semibold text-white/70">{CAMPAIGN.eventName}</span>
            </div>

            <p className="mt-4 text-[11px] font-bold tracking-[0.2em] uppercase text-[var(--z-accent)]">Tawaran Khas</p>
            <h1 className="font-serif-display text-[1.65rem] md:text-[2.1rem] leading-[1.1] font-semibold mt-1" style={{ textWrap: 'balance' }}>
              Sistem Pusat Sumber Digital Zabra{' '}
              <span className="inline-block align-middle font-sans text-[10px] font-bold tracking-[0.18em] text-white/80 border border-white/25 rounded-md px-1.5 py-0.5">LITERA</span>
            </h1>

            <div className="mt-4 border-l-[3px] border-[var(--z-accent)] pl-3.5">
              <p className="text-base font-bold leading-snug text-white">
                Persediaan Awal, <span className="whitespace-nowrap">Permulaan Digital 2027</span>
              </p>
              <p className="mt-1 text-sm leading-relaxed text-white/80">
                Setup selesai tahun ini, guru &amp; murid siap sedia menjelang Januari.
              </p>
            </div>

            <div className="mt-4 pt-4 border-t border-white/10 flex flex-wrap items-center justify-between gap-3">
              <p className="flex items-center gap-1.5 text-xs font-semibold text-white/80">
                <CalendarClock size={14} className="text-[var(--z-accent)]" />
                Tamat dalam <b className="text-white tabular-nums">{cd.d} hari {pad(cd.h)}:{pad(cd.m)}:{pad(cd.s)}</b>
              </p>
              <button type="button" onClick={goToForm}
                className="inline-flex items-center gap-1.5 bg-[var(--z-accent)] text-[var(--z-ink)] text-sm font-bold px-4 py-2 rounded-full hover:bg-[var(--z-accent-600)] transition">
                Saya berminat <ArrowDown size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Form card */}
        <div ref={cardRef} className="scroll-mt-28 bg-white rounded-3xl shadow-2xl ring-1 ring-black/5 p-7 md:p-9 mt-5">
          {waUrl ? (
            <div className="text-center py-6">
              <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-green-100 text-green-700">
                <BadgeCheck size={30} />
              </span>
              <h2 className="font-serif-display text-2xl font-semibold text-[var(--z-ink)] mt-4">Terima kasih, minat anda diterima!</h2>
              <div className="flex items-center justify-center gap-2.5 mt-5 text-sm font-semibold text-[var(--z-muted)]">
                <span className="w-5 h-5 rounded-full border-[3px] border-[var(--z-accent-100)] border-t-[var(--z-accent)] animate-spin" aria-hidden />
                Membuka WhatsApp...
              </div>
              <a href={waUrl} className="inline-block mt-5 text-sm text-[var(--z-accent)] underline">
                Tidak dibuka? Klik di sini
              </a>
            </div>
          ) : (
          <>
          <form onSubmit={handleSubmit} noValidate>
            <div className="mb-5">
              <h2 className="font-serif-display text-xl font-semibold text-[var(--z-ink)]">Daftar Minat</h2>
              <p className="text-sm text-[var(--z-muted)] mt-1">Isi borang ringkas ini untuk dapatkan harga promosi.</p>
            </div>
            {/* Coupon (locked) */}
            <div className="mb-6">
              <label className="block mb-2 font-semibold text-[var(--z-ink)]">Kupon:</label>
              <div className="flex items-center gap-3 p-3 rounded-lg border-2 border-dashed border-[var(--z-accent)] bg-[var(--z-accent-50)]">
                <Gift size={20} className="text-[var(--z-accent-700)] shrink-0" />
                <span className="font-mono font-bold tracking-[0.15em] text-lg text-[var(--z-ink)]">{CAMPAIGN.coupon}</span>
                <span className="ml-auto inline-flex items-center gap-1 text-xs font-bold text-green-700 whitespace-nowrap">
                  <BadgeCheck size={16} /> Kupon sah
                </span>
                <Lock size={14} className="text-[var(--z-muted)]" aria-label="Dikunci" />
              </div>
            </div>

            <div className="mb-4">
              <label htmlFor="name" className="block mb-2 font-semibold text-[var(--z-ink)]">Nama:</label>
              <input id="name" type="text" placeholder="Masukkan nama anda" className={inputCls(errors.name)}
                value={name} onChange={(e) => { setName(e.target.value); clearErr('name'); }} />
              {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
            </div>

            <div className="mb-4">
              <label className="block mb-2 font-semibold text-[var(--z-ink)]">Negeri:</label>
              <div className={`${inputCls(false)} flex items-center justify-between !bg-gray-50 text-gray-600`}>
                Selangor <Lock size={14} className="text-[var(--z-muted)]" />
              </div>
            </div>

            <div className="mb-4">
              <label htmlFor="school" className="block mb-2 font-semibold text-[var(--z-ink)]">Sekolah:</label>
              <div className="relative">
                <input id="school" type="text" autoComplete="off"
                  placeholder={isManualEntry ? 'Masukkan nama sekolah' : 'Cari Sekolah'}
                  value={searchTerm} onChange={handleSearchChange} className={inputCls(errors.school)} />
                {!isManualEntry && suggestions.length > 0 && (
                  <ul className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg mt-1 shadow-lg overflow-hidden">
                    {suggestions.map((s) => (
                      <li key={s} onClick={() => pickSchool(s)} className="p-2.5 text-sm hover:bg-[var(--z-accent-50)] cursor-pointer text-gray-800">
                        {s}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {errors.school && <p className="text-red-500 text-sm mt-1">{errors.school}</p>}
              <button type="button" onClick={toggleManualEntry}
                className="mt-2 text-sm text-[var(--z-accent)] underline hover:text-[var(--z-accent-600)] focus:outline-none text-left">
                {isManualEntry ? 'Cari dari senarai sekolah' : 'Tidak jumpa sekolah anda? Klik di sini untuk masukkan secara manual'}
              </button>
            </div>

            <div className="mb-4">
              <label htmlFor="phoneNumber" className="block mb-2 font-semibold text-[var(--z-ink)]">Nombor Telefon:</label>
              <input id="phoneNumber" type="tel" inputMode="numeric" placeholder="e.g., 012-3456-7890"
                value={phoneNumber} onChange={handlePhone} className={inputCls(errors.phoneNumber)} />
              {errors.phoneNumber && <p className="text-red-500 text-sm mt-1">{errors.phoneNumber}</p>}
            </div>

            <div className="mb-4">
              <label htmlFor="position" className="block mb-2 font-semibold text-[var(--z-ink)]">Jawatan:</label>
              <select id="position" className={inputCls(errors.position)} value={position}
                onChange={(e) => { setPosition(e.target.value); clearErr('position'); }}>
                {POSITIONS.map((p) => <option key={p} value={p}>{p === 'GPM' ? 'GPM (Guru Perpustakaan & Media)' : p}</option>)}
              </select>
              {errors.position && <p className="text-red-500 text-sm mt-1">{errors.position}</p>}
            </div>

            <label htmlFor="under300" className={`mb-6 flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition ${
              under300 ? 'border-[var(--z-accent)] bg-[var(--z-accent-50)]' : 'border-[var(--z-line)] bg-white'
            }`}>
              <input id="under300" type="checkbox" checked={under300} onChange={(e) => setUnder300(e.target.checked)}
                className="mt-0.5 w-5 h-5 shrink-0 accent-[var(--z-accent)]" />
              <span className="text-sm text-[var(--z-ink)]">
                Sekolah saya ada <b>kurang daripada 300 murid</b>
                <span className="block text-xs text-[var(--z-muted)] mt-0.5">Hubungi kami untuk tawaran khas.</span>
              </span>
            </label>

            <button type="submit" disabled={isSubmitting}
              className="btn-accent w-full text-lg disabled:opacity-60 disabled:cursor-not-allowed">
              {isSubmitting ? 'Menghantar...' : 'Daftar Minat via WhatsApp'}
            </button>
            <p className="text-center text-xs text-[var(--z-muted)] mt-3">
              Hantar sebelum <b>{CAMPAIGN.deadlineLabel}</b>. Pilihan tawaran akan dipaparkan selepas borang dihantar.
            </p>
          </form>

          {/* Terms */}
          <div className="mt-7 pt-5 border-t border-[var(--z-line)]">
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[var(--z-muted)] mb-2">Terma &amp; Syarat</p>
            <ul className="text-xs text-[var(--z-muted)] space-y-1.5 list-disc pl-4 leading-relaxed">
              <li>Diskaun untuk Sistem Pusat Sumber Zabra (LITERA) sahaja, satu tebusan bagi setiap sekolah.</li>
              <li>Pilihan tawaran dipaparkan selepas borang dihantar; sekolah memilih mengikut kesesuaian.</li>
              <li>Borang perlu dihantar sebelum {CAMPAIGN.deadlineLabel}, 11:59 malam.</li>
            </ul>
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PpdppPage;
